import React, { useState } from 'react';
import {
  Button,
  Typography,
  Box,
  CircularProgress,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import axios from 'axios';
import Header1 from '../Header/Header1';
import Footer2 from '../Footer/Footer2';
import { useAuth } from '../../AuthContext';

const API_URL = process.env.REACT_APP_API_URL;

const DownloadCenter = () => {
  const [loading, setLoading] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);
  const { userId } = useAuth();

  const handleDownload = async () => {
    if (!userId) {
      alert('User not authenticated');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/export-users/`, {
        params: { user_id: userId },
        responseType: 'blob',
      });

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'users.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading file:', error);
      alert('Failed to download file');
    } finally {
      setLoading(false);
      setOpenConfirm(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        background: 'linear-gradient(135deg, #000000ff, #000000ff, #000000ff)',
      }}
    >
      <Header1 />

      {/* Center Content */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
        }}
      >
        <Card
          sx={{
            maxWidth: 420,
            width: '100%',
            borderRadius: 4,
            backgroundColor: '#121212',
            boxShadow: '0 15px 40px rgba(0,0,0,0.8)',
            border: '1px solid rgba(255,255,255,0.08)',
          }}
        >
          <CardContent sx={{ textAlign: 'center', p: 4 }}>
            <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ color: '#eaeaea' }}>
              Download Center
            </Typography>

            <Typography variant="body1" sx={{ color: '#b0b0b0', mb: 4 }}>
              Export all user data securely in Excel format.
            </Typography>

            <Button
              fullWidth
              size="large"
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={() => setOpenConfirm(true)}
              sx={{
                py: 1.5,
                fontSize: '1rem',
                borderRadius: 3,
                background: 'linear-gradient(90deg, #00c6ff, #0072ff)',
                boxShadow: '0 8px 20px rgba(0,114,255,0.4)',
                '&:hover': {
                  background: 'linear-gradient(90deg, #00b4ff, #005ce6)',
                },
              }}
            >
              Download Excel
            </Button>
          </CardContent>
        </Card>
      </Box>

      {/* Confirmation Dialog */}
      <Dialog
        open={openConfirm}
        onClose={() => !loading && setOpenConfirm(false)}
        PaperProps={{
          sx: {
            backgroundColor: '#1e1e1e',
            color: '#eaeaea',
            borderRadius: 3,
            border: '1px solid rgba(255,255,255,0.08)',
          },
        }}
      >
        <DialogTitle>Confirm Download</DialogTitle>

        <DialogContent>
          <Typography sx={{ color: '#b0b0b0' }}>
            Are you sure you want to download the user data in Excel format?
          </Typography>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setOpenConfirm(false)}
            disabled={loading}
            sx={{ color: '#b0b0b0' }}
          >
            Cancel
          </Button>

          <Button
            onClick={handleDownload}
            variant="contained"
            disabled={loading}
            sx={{
              background: 'linear-gradient(90deg, #00c6ff, #0072ff)',
              '&:hover': {
                background: 'linear-gradient(90deg, #00b4ff, #005ce6)',
              },
            }}
          >
            {loading ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      <Footer2 />
    </Box>
  );
};

export default DownloadCenter;
